import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Mail, Loader2, CheckCircle, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestEditLink() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editLink, setEditLink] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error("Veuillez entrer votre email");
      return;
    }

    setLoading(true);
    
    try {
      const res = await axios.post(`${API}/businesses/request-edit`, { email });
      setSuccess(true);
      // For demo, show the edit link directly
      if (res.data.edit_token) {
        setEditLink(`${window.location.origin}/modifier/${res.data.edit_token}`);
      }
      toast.success("Lien de modification envoyé!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center px-6">
        <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
            </Link>
          </div>
        </header>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card rounded-3xl p-8 md:p-12 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-4">
            Lien envoyé !
          </h1>
          <p className="text-gray-600 mb-6">
            Un lien de modification a été envoyé à votre adresse email. Il est valide pendant 24 heures.
          </p>
          
          {/* Demo: Direct link */}
          {editLink && (
            <div className="bg-lagoon-50 rounded-xl p-4 mb-6">
              <p className="text-sm text-lagoon-900 mb-2 font-medium">
                Lien de modification (démo) :
              </p>
              <a 
                href={editLink}
                className="text-coral hover:text-coral-600 text-sm break-all underline"
                data-testid="edit-link"
              >
                Cliquez ici pour modifier votre fiche
              </a>
            </div>
          )}
          
          <Link to="/">
            <Button className="btn-primary">Retour à l'accueil</Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center px-6">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-lagoon-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-lagoon flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-serif text-xl font-semibold text-lagoon-900">Annuaire Polynésien</span>
          </Link>
        </div>
      </header>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card rounded-3xl p-8 md:p-12 max-w-md w-full"
      >
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-lagoon-600 hover:text-lagoon-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour</span>
        </Link>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-lagoon-50 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-lagoon" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-lagoon-900 mb-2">
            Modifier ma fiche
          </h1>
          <p className="text-gray-600">
            Entrez l'email utilisé lors de votre inscription pour recevoir un lien de modification
          </p>
        </div>

        <form onSubmit={handleSubmit} data-testid="request-edit-form">
          <div className="mb-6">
            <Label htmlFor="email" className="text-lagoon-900 font-semibold">
              Adresse email
            </Label>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 h-12 bg-white/50 border-lagoon-100 rounded-xl"
                placeholder="contact@entreprise.pf"
                data-testid="email-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="btn-coral w-full h-12"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Envoi en cours...
              </>
            ) : (
              "Recevoir le lien de modification"
            )}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
